import { Button, Dialog, DialogActions, Divider, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PimDashboardState } from "context/PimDashboardContext";
import ImageSlider from "components/PimDashboard/CommonComponents/ImageSlider/ImageSlider";
import { AddRounded, EditRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { costingTableTypes, supplierColumn, totalQtyColumn } from "./data";
import { useEffect, useState } from "react";
import AddFileButton from "components/PimDashboard/CommonComponents/AddFileButton/AddFileButton";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { custom } from "data/authData";
import DisplayDetailsWithEditTab from "./DisplayDetailsWithEditTab/DisplayDetailsWithEditTab";
import FabricTable from "../../../CreateShootCosting/CostTable/CostTable";

import {
	applyDiscountingCol,
	costingColumns,
	costingShootAssocObjKey,
	openCostingTrimsDynamicConsumpAssocObjKey,
	paymentTermColumn,
	shootCostingItemsAssocObjKey,
	shootCostingUrlKey,
} from "../../../CreateShootCosting/data";
import CostTable from "../../../CreateShootCosting/CostTable/CostTable";
import ShootSelection from "../../../CreateShootCosting/ShootSelection/ShootSelection";
import { inputTypes } from "data/globalData";
import { constraintsOptions, isColumnDefault } from "components/PimDashboard/data";

const imgWidth = 170;
const imgHeight = 210;

const CostingDetails = ({ selectedCosting, setSelectedCosting }) => {
	const { allAttributesObj: styleAllAttributesObj } = PimDashboardState();
	const { snackBar, setLoadingOpen, setSnackBarOpen, setSnackBar } = GlobalState();
	const { user } = AuthState();
	const navigate = useNavigate();
	let [searchParams, setSearchParams] = useSearchParams();

	const disableEdit = user?.role?.[custom.SHOOT_COSTING_VIEWER.key] === true;

	const [loading, setLoading] = useState({});

	const [optionDetailsColumns, setOptionDetailsColumns] = useState([]);

	const [selectedShoots, setSelectedShoots] = useState([]);
	const [editShootsModal, setEditShootsModal] = useState(false);
	const [editedShootsList, setEditedShootsList] = useState([]);

	const [costingItemRows, setCostingItemRows] = useState([]);
	const [costingItemTotalRow, setCostingItemTotalRow] = useState({ id: new Date().valueOf() });

	const [editMode, setEditMode] = useState("");

	const handleShootsEditModalOpen = () => {
		setEditShootsModal(true);
		setEditedShootsList([...selectedShoots]);
	};
	const handleShootsEditModalClose = () => {
		setEditShootsModal(false);
		setEditedShootsList([]);
	};

	useEffect(() => {
		if (styleAllAttributesObj && !optionDetailsColumns?.length) {
			const columns = Object.values(costingColumns);
			setOptionDetailsColumns(columns);
		}
	}, []);

	useEffect(() => {
		const fetch = async () => {
			setLoadingOpen(true);
			setLoading(true);
			setSelectedCosting();
			try {
				const res = await axios.get(`/api/studio/shoot_costing_by_id/${id}`);
				const result = res.data.result;
				setSelectedCosting(() => {
					let obj = { ...result };
					delete obj[shootCostingItemsAssocObjKey];
					delete obj[costingShootAssocObjKey];
					return obj;
				});
				setCostingItemRows(result[shootCostingItemsAssocObjKey] ?? []);
				setSelectedShoots(result[costingShootAssocObjKey] ?? []);

				setLoadingOpen(false);
				setLoading(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
				setLoadingOpen(false);
				setLoading(false);
			}
		};
		let id = searchParams.get(shootCostingUrlKey);
		if (id && id !== selectedCosting?.id) fetch();
		else {
			setSelectedCosting(null);
		}
	}, [searchParams]);

	const handleCostingDetailsSave = async (data, closeModal) => {
		console.log(data, "xcvbnm");
		setLoadingOpen(true);
		try {
			const pendingFields = [];
			Object.values(costingColumns)
				.filter((row) => row?.input_type !== inputTypes.AUTO_GENERATE.key)
				.forEach((attr) => {
					let obj = data;
					if (isColumnDefault(attr, obj?.[attr?.key]) && attr?.constraints[constraintsOptions.NOTNULL.key])
						pendingFields.push(attr);
				});
			if (pendingFields?.length) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: `Enter values for ${pendingFields.map((row) => row?.name)}`,
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				return;
			}
			let body = {};
			Object.values(costingColumns)
				.filter((row) => row?.input_type !== inputTypes.AUTO_GENERATE.key)
				.forEach((col) => {
					body[col.key] = data[col.key];
				});
			await axios.put(`/api/studio/update_shoot_costing_details/${data?.shoot_costing_id}`, body).then((res) => {
				setSelectedCosting((prev) => ({ ...prev, ...body }));
				setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: `Data Saved Successfully`,
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				closeModal();
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handleCostingItemsSave = async (data, deletedRowIds) => {
		const res = await axios.put(`/api/studio/update_shoot_costing_items/${selectedCosting?.shoot_costing_id}`, {
			rows: data,
			shoot_costing: { total_cost: costingItemTotalRow?.value || 0 },
		});
		return res.data.result;
	};

	const handleUpdatedShootsSave = async () => {
		try {
			setLoadingOpen(true);
			if (!editedShootsList?.length) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: "Please select atleast one shoot", severity: "error" });
				setLoadingOpen(false);
				return;
			}

			await axios
				.put(
					`/api/studio/update_shoot_modal_profile/${selectedCosting.shoot_costing_id}`,
					editedShootsList?.map((row) => row.shoot_id)
				)
				.then((res) => {
					setSelectedShoots(editedShootsList);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Shoots Updated Successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
			setLoadingOpen(false);
			handleShootsEditModalClose();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	if (loading) return <></>;
	else
		return (
			<Stack spacing={2} pt={2} pb={10}>
				<DisplayDetailsWithEditTab
					columns={optionDetailsColumns}
					data={selectedCosting}
					setData={setSelectedCosting}
					updateFunction={handleCostingDetailsSave}
					disableEdit={disableEdit}
				/>
				<Box width="99%">
					<Box width="100%" display="flex" justifyContent="space-between">
						<Typography variant="h6">Shoots</Typography>
						<Button
							variant="contained"
							disabled={disableEdit}
							onClick={handleShootsEditModalOpen}
							size="small"
							startIcon={<EditRounded />}>
							Edit
						</Button>
					</Box>
					<ShootSelection
						selectedShoots={selectedShoots}
						setSelectedShoots={setSelectedShoots}
						costingDetails={selectedCosting}
						disabled={true}
						height={250}
						hideInput={true}
					/>
				</Box>
				<Box width="99%">
					<CostTable
						rows={costingItemRows}
						setRows={setCostingItemRows}
						totalRow={costingItemTotalRow}
						setTotalRow={setCostingItemTotalRow}
						editMode={editMode === costingTableTypes.main_cost.key}
						setEditMode={(mode) => setEditMode(mode ? costingTableTypes.main_cost.key : "")}
						mode={editMode === costingTableTypes.main_cost.key || editMode === "" ? "edit" : ""}
						handleSave={handleCostingItemsSave}
					/>
				</Box>

				<Dialog fullWidth maxWidth="md" open={editShootsModal} onClose={handleShootsEditModalOpen}>
					<Box width="98%" p={2} boxSizing="border-box">
						<ShootSelection
							selectedShoots={editedShootsList}
							setSelectedShoots={setEditedShootsList}
							costingDetails={selectedCosting}
						/>
					</Box>
					<DialogActions>
						<Button variant="outlined" onClick={handleShootsEditModalClose}>
							Cancel
						</Button>
						<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleUpdatedShootsSave}>
							Save
						</Button>
					</DialogActions>
				</Dialog>
			</Stack>
		);
};

export default CostingDetails;
